// WHAT: Loading skeleton component for dashboard widgets
// WHY:  Provides consistent loading state UI for widgets during data fetching
// DOMAIN: Core → Dashboard → Widgets → Base
// DEPENDENCIES: Card, Skeleton components
// RISKS:
//   - NOTE: Size mapping is fixed - may not match all widget sizes
//   - NOTE: Skeleton layout may not match actual widget layout
//   - NOTE: Animation (pulse) may cause performance issues on many widgets

import { type FC } from "react";

import { Card, Skeleton } from "@/components/ui";
import { cn } from "@/shared/utils";

/**
 * Props for WidgetLoader component
 */
interface WidgetLoaderProperties {
  /** Additional CSS classes */
  className?: string;
  /** Widget size */
  size?: "large" | "medium" | "small" | "xlarge";
}

/**
 * WHAT:
 *   Loading skeleton component that provides consistent loading state UI
 *   for dashboard widgets during data fetching.
 *
 * WHY:
 *   Ensures consistent loading experience across all widgets with appropriate
 *   skeleton placeholders.
 *
 * BEHAVIOR:
 *   - Displays skeleton header
 *   - Shows skeleton content lines
 *   - Optionally shows footer skeleton for larger widgets
 *   - Adapts height based on size prop
 *
 * RISKS:
 *   - NOTE: Size mapping is fixed - may not match all widget sizes
 *   - NOTE: Skeleton layout may not match actual widget layout
 *   - NOTE: Animation (pulse) may cause performance issues on many widgets
 *
 * @param root0 - Component props
 *
 * @param root0.size - Widget size
 *
 * @param root0.className - Additional CSS classes
 *
 * @returns {JSX.Element} Widget loader skeleton component
 *
 */
export const WidgetLoader: FC<WidgetLoaderProperties> = ({ className, size = "medium" }) => {
  const heightMap = {
    large: "h-64",
    medium: "h-48",
    small: "h-32",
    xlarge: "h-80",
  };

  return (
    <Card
      className={cn("animate-pulse", heightMap[size], className)}
      padding="md"
      variant="default"
    >
      <div className="space-y-4">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-4 rounded" />
        </div>

        {/* Content Skeleton */}
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>

        {/* Footer Skeleton (for larger widgets) */}
        {(size === "large" || size === "xlarge") && (
          <div className="space-y-2 mt-4">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
          </div>
        )}
      </div>
    </Card>
  );
};
